import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { 
  Button, TextField, Box, Divider, Typography, CircularProgress, 
  Checkbox, FormControlLabel, Paper, MenuItem, Select, InputLabel, 
  FormControl, Tabs, Tab, Grid
} from "@material-ui/core";
import { getValueByKey, displayDateByLanguage, formatToIsoDate } from "../../Utils/utils";

import { connect } from "react-redux";
import { editComposer, resetData } from "../../Redux/Actions/dataActions";

// Import React FilePond
import { FilePond, registerPlugin } from "react-filepond";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import "./filepond.min.css";

registerPlugin(FilePondPluginFileValidateType);

function MdlComposerEdit(props) {
  const { t } = useTranslation();
  // 1. Zentraler Formular-State
  const [composerData, setComposerData] = useState({
    disambiguation: "",
    gender: "",
    countryCode: "",
    type: "",
    area: "",
    imdbUrl: "",
    artistId: "",
    lifespanBegin: "1990-01-01",
    lifespanEnd: "1990-01-01",
    lifespanIsEnded: false,
    portraitUrl: ""
  });

  const [overwriteFields, setOverwriteFields] = useState({});
  const userLang = props.userData?.language || 'de';
  const emptyDate = "1990-01-01";
  const [disabled, setDisabled] = useState(true);
  const hasTriggeredLoadRef = useRef(false);

  const [composerOptions, setComposerOptions] = useState([]);

  const fieldDefinitions = [
    { labelKey: "composer.disambiguation", label: t("composer.disambiguation"), key: "disambiguation", type: "text" },
    { labelKey: "composer.gender", label: t("composer.gender"), key: "gender", type: "dropdown", optionType: "gender" },
    { labelKey: "composer.type", label: t("composer.type"), key: "type", type: "dropdown", optionType: "artisttype" },
    { labelKey: "composer.area", label: t("composer.area"), key: "area", type: "text" },
    { labelKey: "composer.countryCode", label: t("composer.countryCode"), key: "countryCode", type: "text" },
    { labelKey: "composer.lifespanBegin", label: t("composer.lifespanBegin"), key: "lifespanBegin", type: "date" },
    { labelKey: "composer.lifespanEnd", label: t("composer.lifespanEnd"), key: "lifespanEnd", type: "date" },
    { labelKey: "composer.artistId", label: t("composer.artistId"), key: "artistId", type: "text" },
    { labelKey: "composer.imdbUrl", label: t("composer.imdbUrl"), key: "imdbUrl", type: "text" },
    { labelKey: "composer.lifespanIsEnded", label: t("composer.lifespanIsEnded"), key: "lifespanIsEnded", type: "checkbox" },
  ];
  
  useEffect(() => {
    if (!userLang) return;
    const fetchArtistTypes = axios.get(`/settings/options?type=artisttype&lang=${userLang}`);
    const fetchGenders     = axios.get(`/settings/options?type=gender&lang=${userLang}`);

    // Promise.all feuert beide Abfragen zeitgleich ab (schneller!)
    Promise.all([fetchArtistTypes, fetchGenders])
      .then(([artistRes, genderRes]) => {
        const artistData = artistRes.data || [];
        const genderData = genderRes.data || [];

        const combinedOptions = [...artistData, ...genderData];

        setComposerOptions(combinedOptions);
        console.log("✅ Optionen erfolgreich gemischt im Speicher:", combinedOptions);
      })
      .catch(err => {
        console.error("Fehler beim parallelen Laden der Dropdown-Optionen:", err);
      });

  }, [userLang]); 
  
  // Zustände für die interaktive Trefferliste
  const [loadingApi, setLoadingApi] = useState(false);
  const [searchResults, setSearchResults] = useState([]); 
  const [selectedExternalData, setSelectedExternalData] = useState(null); 

  // Bild-Upload Zustände
  const [files, setFiles] = useState([]);
  const [imageUploadMethod, setImageUploadMethod] = useState(0); // 0 = URL, 1 = File (Für Tabs)
  const [filePreviewUrl, setFilePreviewUrl] = useState(""); // Lokale Vorschau für FilePond
  const [uploadFile, setUploadFile] = useState(undefined);

  // Pflichtfeld-Validierung vor Freischaltung des Speichern-Buttons
/*  useEffect(() => {
    const hasComposer = composerData.composer && composerData.composer.trim() !== "";
    const hasSheetName = composerData.sheetName && composerData.sheetName.trim() !== "";
    const hasFile = composerData.uploadFile !== undefined;
    setDisabled(!(hasComposer && hasSheetName && hasFile));
  }, [composerData.composer, composerData.sheetName, composerData.uploadFile]);
 */

  useEffect(() => {
    const hasComposer = props.composer.name && props.composer.name.trim() !== "";
 //   const hasSheetName = composerData.sheetName && composerData.sheetName.trim() !== "";
 //   const hasFile = composerData.uploadFile !== undefined;
    setDisabled(!(hasComposer));
  }, [props.composer.name]);


  // Initialisiert die Überschreib-Flags
  useEffect(() => {
    const keysInComposer = Object.keys(composerData);
    const initialOverwrite = {};
    keysInComposer.forEach((key) => {
      initialOverwrite[key] = false;
    });
    setOverwriteFields(initialOverwrite);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const [hasLoaded, setHasLoaded] = useState(false); 

  // Einmaliges Laden der "aktuellen" Stamm-Daten aus der SQLite-Datenbank
  useEffect(() => {
	if (hasTriggeredLoadRef.current) return;

    const composerSafeName = props.composer?.safe_name || props.composer?.SafeName;
    if (!composerSafeName || hasLoaded) return;

	hasTriggeredLoadRef.current = true; 

    axios.get(`/composer/${composerSafeName}`)
      .then(res => {
        if (res.data) {
          // 💡 KORREKTUR 1: Wandelt SQLite-Booleans (1/0 oder Strings) in echte JS-Booleans um
          const isEndedBool = res.data.lifespanIsEnded === true || res.data.lifespanIsEnded === 1 || res.data.lifespanIsEnded === "true";

          setComposerData({
            disambiguation: res.data.disambiguation || "",
            gender: res.data.gender || "",
            type: res.data.type || "",
            area: res.data.area || "",
            countryCode: res.data.countryCode || "",
            imdbUrl: res.data.imdbUrl || "",
            artistId: res.data.artistId || "",
			lifespanBegin: res.data.lifespanBegin ? res.data.lifespanBegin : emptyDate, // formatToIsoDate(res.data.lifespanBegin, emptyDate),
			lifespanEnd: res.data.lifespanEnd|| emptyDate, //formatToIsoDate(res.data.lifespanEnd, emptyDate),
            lifespanIsEnded: isEndedBool, 
            portraitUrl: res.data.portraitUrl || ""
          });
          setHasLoaded(true); 
        }
      })
      .catch((err) => {
        console.error("Fehler beim Live-Abruf der Formulardaten:", err);
        // Fallback aus Props ebenfalls absichern
        if (props.composer) {
          const isEndedBool = props.composer.lifespanIsEnded === true || props.composer.lifespanIsEnded === 1;
          setComposerData({
            disambiguation: props.composer.disambiguation || "",
            gender: props.composer.gender || "",
            type: props.composer.type || "",
            area: props.composer.area || "",
            countryCode: props.composer.countryCode || "",
            imdbUrl: props.composer.imdbUrl || "",
            artistId: props.composer.artistId || "",
            lifespanBegin: props.composer.lifespanBegin || emptyDate,
            lifespanEnd: props.composer.lifespanEnd || emptyDate,
            lifespanIsEnded: isEndedBool, 
            portraitUrl: props.composer.portraitUrl || ""
          });
        }
        setHasLoaded(true);
      });
  }, [props.composer, hasLoaded]);
  
    console.log("Erfolgreich gefundener Composer aus DB:", composerData);

	// A. Der Hook, der auf FilePond-Änderungen lauscht und das Canvas triggert
	useEffect(() => {
	  async function processFile() {
		// Schaut nach, ob in FilePond (files[0]) ein valides Bild liegt
		if (files && files[0] && files[0].file) {
		  const fileData = await readPhoto(files[0].file);
		  const resizedImage = new File(
			[dataURItoBlob(fileData)],
			"name.png"
		  );
		  setUploadFile(resizedImage); // 💡 Befüllt das File für dein handleSave
		  
		  const objectUrl = URL.createObjectURL(resizedImage);
		  setFilePreviewUrl(objectUrl);
		} else {
		  setUploadFile(undefined);
		  setFilePreviewUrl("");
		}
	  }
	  
	  processFile();
	}, [files]); 

	// B. Bild auf 200x200 Pixel herunterskalieren via HTML5-Canvas
	const readPhoto = async (photo) => {
	  const canvas = document.createElement("canvas");
	  const img = document.createElement("img");

	  img.src = await new Promise((resolve) => {
		const reader = new FileReader();
		reader.onload = (e) => resolve(e.target.result);
		reader.readAsDataURL(photo);
	  });
	  await new Promise((resolve) => {
		img.onload = resolve;
	  });

	  canvas.width = 200;
	  canvas.height = 200;
	  canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
	  return canvas.toDataURL("image/png");
	};

	// C. Daten-URI für das Go-Backend in ein echtes Blob umwandeln
	function dataURItoBlob(dataURI) {
	  var byteString = atob(dataURI.split(",")[1]);
	  var mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];
	  var ab = new ArrayBuffer(byteString.length);
	  var ia = new Uint8Array(ab);
	  for (var i = 0; i < byteString.length; i++) {
		ia[i] = byteString.charCodeAt(i);
	  }
	  return new Blob([ab], { type: mimeString });
	}

	const handleInputChange = (e) => {
		const { name, value, type, checked } = e.target;
		setComposerData(prev => ({
		  ...prev,
		  [name]: type === "checkbox" ? checked : value
		}));
	};

  const handleSave = (e) => {
	e.preventDefault();

	const targetSafeName = props.composer?.safe_name || props.composer?.SafeName;
	const bodyFormData = new FormData();

	// 1. Kopie des aktuellen Formular-States erstellen
	let cleanData = { ...composerData };

	// 2. ⭐ DYNAMISCHE DATUMS-PRÜFUNG: Nutzt deine fieldDefinitions!
	fieldDefinitions.forEach((field) => {
		if (field.type === "date") {
		  const currentValue = cleanData[field.key];
		  
		  // Wenn das Datum leer, undefiniert oder ein ungültiger String ist -> emptyDate setzen
		  if (!currentValue || currentValue.trim() === "" || currentValue === "undefined") {
			cleanData[field.key] = emptyDate;
		  }
		}
	});

	// Logischer Sonderfall für das Karriere-Ende: 
	// Wenn die Karriere laut Checkbox NICHT beendet ist, zwingen wir das Ende auf das emptyDate
	if (!cleanData.lifespanIsEnded) {
	cleanData.lifespanEnd = emptyDate;
	}

	// 3. Alle bereinigten Felder in das FormData-Objekt übertragen
	Object.keys(cleanData).forEach(key => {
		if (cleanData[key] !== undefined && cleanData[key] !== null) {
		  bodyFormData.append(key, cleanData[key]);
		}
	});
  
    // Falls der Datei-Upload (Tab 1) aktiv ist, hängen wir das Canvas-Bild an
    if (imageUploadMethod === 1 && uploadFile) {
      bodyFormData.append("portrait", uploadFile); // lauscht auf form:"portrait"
      console.log("📁 Canvas-Bild (portrait) wurde angehängt.");
    }

    // Übergabe an die Redux-Action
    props.editComposer(bodyFormData, targetSafeName);
    props.onClose(); 
  };
  
  const handleFetchExternalData = async () => {
    setLoadingApi(true);
    setSearchResults([]);
    setSelectedExternalData(null);
    try {
      const searchComposer = props.composer?.name || "";
      const queryString1 = "query=artist:";
      const escapedQuery = encodeURIComponent(searchComposer);
      const queryString = queryString1 + escapedQuery;
      const params = new URLSearchParams();
      params.append("term", queryString);

      const res = await axios.get(`/composer/autofill?${params.toString()}`);
      if (res.data && Array.isArray(res.data)) {
        setSearchResults(res.data);
      }
    } catch (err) {
      console.error("Fehler beim Abrufen der externen Metadaten:", err);
    } finally {
      setLoadingApi(false);
    }
  };

  const handleSelectApiResult = (artist) => {
    setSelectedExternalData({
      disambiguation: artist.Disambiguation || "-",
      gender: artist.Gender || "-",
      type: artist.Type || "-",
      area: artist.AreaName || "-",
      countryCode: artist.CountryCode || "-",
      imdbUrl: artist.IMDbUrl || "-",
      artistId: artist.MbArtistID || "-",
      lifespanBegin: artist.LifeSpanBegin ? formatToIsoDate(artist.LifeSpanBegin) : "", 
      lifespanEnd: formatToIsoDate(artist.LifespanEnd) || "",     // 💡 Gesichert
      lifespanIsEnded: artist.LifeSpanEnd || false, 
      portraitUrl: artist.PortraitUrl || ""
    });
  };

  const handleOverwriteCheckboxChange = (field) => (event) => {
    const checked = event.target.checked;
    setOverwriteFields(prev => ({ ...prev, [field]: checked }));

    if (checked && selectedExternalData) {
      setComposerData(prev => ({
        ...prev,
        [field]: selectedExternalData[field] !== "-" ? selectedExternalData[field] : prev[field]
      }));
    }
  };

  return (
    <Box 
      style={{ 
        width: "100%", 
        maxWidth: "460px", 
        margin: "0 auto", 
        padding: "10px 5px", 
        maxHeight: "70vh", 
        overflowY: "auto" 
      }}
    >
      <form noValidate autoComplete="off" style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <Box style={{ display: "flex", flexDirection: "column", gap: "16px", position: "relative" }}>
          
          {/* SEKTION 1: MUSICBRAINZ SUCHABFRAGE */}
          <Paper style={{ padding: "16px", backgroundColor: "#f9f9fc" }} variant="outlined">
			<Typography variant="caption" style={{ color: "#4f40b3", fontWeight: "bold", letterSpacing: "1px", textTransform: "uppercase" }}>
               {t("common.search_musicbrainz")}
            </Typography>
            <Box display="flex" alignItems="center" gap="15px" marginBottom="12px">
              <Button 
                variant="contained" 
                onClick={handleFetchExternalData}
                disabled={loadingApi}
                style={{ backgroundColor: "#4f40b3", color: "white", textTransform: "none" }}
              >
                {loadingApi ? <CircularProgress size={20} color="inherit" /> : t("common.online_query")}
              </Button>
            </Box>

            {/* Optimierung 3: Strukturierte Trefferliste */}
            {searchResults.length > 0 && (
              <Box style={{ marginBottom: "15px" }}>
                <Typography variant="caption" style={{ fontWeight: "bold", color: "#1d1b31" }}>{t("common.search_results")}:</Typography>
                <Paper variant="outlined" style={{ maxHeight: "200px", overflowY: "auto", marginTop: "5px", backgroundColor: "#fff" }}>
                  {searchResults.map((artist, idx) => (
                    <Box 
                      key={idx} 
                      onClick={() => handleSelectApiResult(artist)}
                      style={{ 
                        padding: "12px", 
                        cursor: "pointer", 
                        borderBottom: "1px solid #eee",
                        backgroundColor: selectedExternalData?.artistId === artist.MbArtistID ? "#eae6ff" : "transparent",
                        transition: "background-color 0.2s"
                      }}
                    >
                      {/* Zeile 1: Name des Künstlers permanent fett */}
                      <Typography variant="body1" style={{ fontWeight: "bold", color: "#1d1b31" }}>
                        {artist.Name}
                      </Typography>
                      {/* Zeile 2: Erläuterung (Disambiguation) */}
                      <Typography variant="body2" style={{ color: "#4f40b3", fontSize: "13px", marginTop: "2px" }}>
                        {artist.Disambiguation ? `💡 ${artist.Disambiguation}` : t("common.no_values")}
                      </Typography>
                      {/* Zeile 3: Region / Ort */}
                      <Typography variant="caption" style={{ color: "gray", display: "block", marginTop: "2px" }}>
                        📍 Gebiet/Ort: {artist.AreaName || t("common.not_defined")} {artist.CountryCode ? `(${artist.CountryCode})` : ""}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              </Box>
            )}
			{/* Sektion 1: Abgleich-Vorschau */}
			{selectedExternalData && (
			  <Box style={{ display: "flex", flexDirection: "column", gap: "6px", marginTop: "10px" }}>
				<Divider style={{ marginBottom: "8px" }} />
				<Typography variant="caption" style={{ fontWeight: "bold", color: "#4f40b3" }}>
				  {t("common.overwrite_values")}?
				</Typography>

				{fieldDefinitions.map((field) => {
				  
				  // 💡 DIE ZENTRALE FORMATIERUNGS-REGEL (Verhindert Fehler & Überlagerungen)
				  const formatValueForDisplay = (rawValue) => {
					if (!rawValue || rawValue === "-" || rawValue === "1990-01-01") return "-";
					
					if (field.type === "date") {
					  return displayDateByLanguage(rawValue, userLang);
					}
					
					if (field.type === "dropdown") {
					  // Holt den lesbaren, übersetzten Begriff (z.B. "Einzelperson" statt technischem "Person")
					  // Es prüft sowohl deine geladenen Kategorien als auch gemischte Optionen
					  const foundOption = composerOptions.find(
						opt => opt.key === rawValue || opt.value === rawValue || opt.key === rawValue.toLowerCase()
					  );
					  return foundOption ? foundOption.value : rawValue;
					}
					
					return rawValue;
				  };

				  const localDisplay = formatValueForDisplay(composerData[field.key]);
				  const externalDisplay = formatValueForDisplay(selectedExternalData[field.key]);

				  return (
					<Box 
					  key={field.key} 
					  style={{ 
						display: "flex", 
						flexDirection: "column", 
						gap: "2px", 
						backgroundColor: "#fff", 
						padding: "6px", 
						borderRadius: "4px", 
						border: "1px solid #e2e8f0" 
					  }}
					>
					  <FormControlLabel
						control={
						  <Checkbox 
							checked={!!overwriteFields[field.key]} 
							onChange={handleOverwriteCheckboxChange(field.key)} 
							color="primary" 
							size="small" 
						  />
						}
						label={<Typography variant="caption" style={{ fontWeight: "bold" }}>{field.label}</Typography>}
					  />
					  <Box style={{ display: "flex", justifyContent: "space-between", paddingLeft: "30px" }}>
						<Typography variant="caption" style={{ color: "#cc0000", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "45%" }}>
						  {t("common.old_value")}: {localDisplay}
						</Typography>
						<Typography variant="caption" style={{ color: "#00b300", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "45%" }}>
						  {t("common.new_value")}: {externalDisplay}
						</Typography>
					  </Box>
					</Box>
				  );
				})}
			  </Box>
			)}
          </Paper>

		  <Divider style={{ margin: "5px 0" }} />

          {/* SEKTION 2: BILD-UPLOAD SEKTION MIT SLIDER (TABS) & PREVIEW */}
          <Paper style={{ padding: "16px" }} variant="outlined">
			<Typography variant="caption" style={{ color: "#4f40b3", fontWeight: "bold", letterSpacing: "1px", textTransform: "uppercase" }}>
              {t("common.composer")} {t("files.select_image")}
            </Typography>
            
            <Tabs
              value={imageUploadMethod}
              onChange={(e, newValue) => setImageUploadMethod(newValue)}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              style={{ marginBottom: "15px", backgroundColor: "#f4f3f9", borderRadius: "8px" }}
            >
              <Tab label={t("files.upload_url")} style={{ textTransform: "none", fontWeight: "bold" }} />
              <Tab label={t("files.upload_file")} style={{ textTransform: "none", fontWeight: "bold" }} />
            </Tabs>

            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={imageUploadMethod === 0 ? 8 : 7}>
                {imageUploadMethod === 0 ? (
                  <TextField
                    label={t("files.url_to_image")}
                    name="portraitUrl"
                    onChange={handleInputChange}
                    variant="outlined"
                    fullWidth
                    placeholder="https://example.com"
                  />
                ) : (
                  <FilePond
                    files={files}
                    onupdatefiles={setFiles}
                    allowMultiple={false}
                    maxFiles={1}
                    labelIdle={t("files.file_to_image")}
                    acceptedFileTypes={["image/jpeg", "image/png", "image/webp"]}
                  />
                )}
              </Grid>

              {/* Live-Vorschau inkl. Vorbelegungs-Unterstützung */}
              <Grid item xs={12} sm={imageUploadMethod === 0 ? 4 : 5} style={{ display: "flex", justifyContent: "center" }}>
                <Box style={{ textAlign: "center" }}>
                  <Typography variant="caption" style={{ display: "block", color: "gray", marginBottom: "4px", fontWeight: "bold" }}>
                    {t("files.live_preview")}:
                  </Typography>
                  {imageUploadMethod === 0 && composerData.portraitUrl ? (
                    <img 
                      src={composerData.portraitUrl.startsWith("http") ? composerData.portraitUrl : `/composer/portrait/${props.composer?.safe_name || props.composer?.SafeName}?t=${new Date().getTime()}`} 
                      alt={t("files.preview_url")}  
                      style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: "8px", border: "2px solid #4f40b3" }}
                      onError={(e) => { e.target.src = "https://placeholder.com"; }}
                    />
                  ) : imageUploadMethod === 1 && filePreviewUrl ? (
                    <img 
                      src={filePreviewUrl} 
                      alt={t("files.preview_file")} 
                      style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: "8px", border: "2px solid #4f40b3" }} 
                    />
                  ) : (
                    <Box style={{ width: "100px", height: "100px", backgroundColor: "#eee", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", color: "gray", fontSize: "11px" }}>
                      {t("files.no_image")}
                    </Box>
                  )}
                </Box>
              </Grid>

            </Grid>
          </Paper>

          <Divider style={{ margin: "5px 0" }} />

          {/* SEKTION 3: MANUELLE FIELD MANIPULATION (Vollkommen dynamisch auf Basis der fieldDefinitions) */}
          <Paper style={{ padding: "20px", backgroundColor: "#fafafa" }} variant="outlined">
            <Typography variant="caption" style={{ color: "#4f40b3", fontWeight: "bold", letterSpacing: "1px", textTransform: "uppercase", display: "block", marginBottom: "20px" }}>
              {t("MdlComposerEdit.subtitel_section_3")}
            </Typography>
            
            <Box display="flex" flexDirection="column" gap="16px">
              {fieldDefinitions.map((field) => {
                
                // 1. FALL: Einfache Textfelder (Erläuterung, Wirkungsort, Ländercode, Künstler-ID)
                if (field.type === "text") {
                  return (
                    <Box key={field.key} style={{ marginBottom: "16px" }}>
                      <TextField 
                        fullWidth 
                        variant="outlined" 
                        size="small" 
                        label={<Typography variant="caption" style={{ fontWeight: "bold" }}>{t(field.labelKey)}</Typography>} 
                        name={field.key} 
                        value={composerData[field.key] || ""} 
                        onChange={handleInputChange} 
                        inputProps={field.key === "countryCode" ? { maxLength: 2 } : {}}
                      />
                    </Box>
                  );
                }

                // 2. FALL: Datumsangaben (Karriere Beginn, Karriere Ende)
                if (field.type === "date") {
                  return (
                    <Box key={field.key} style={{ marginBottom: "16px" }}>
                      <TextField 
                        fullWidth 
                        variant="outlined" 
                        size="small" 
                        type="date"
                        label={<Typography variant="caption" style={{ fontWeight: "bold" }}>{t(field.labelKey)}</Typography>} 
                        name={field.key} 
                        value={formatToIsoDate(composerData[field.key])} 
                        onChange={handleInputChange} 
                        InputLabelProps={{ shrink: true }}
                        disabled={field.key === "lifespanEnd" ? !composerData.lifespanIsEnded : false}
                      />
                    </Box>
                  );
                }

                // 3. FALL: Checkboxen (Karriere Beendet)
				if (field.type === "checkbox") {
				  return (
					  <Box key={field.key} style={{ marginBottom: "16px" }}>
						<FormControlLabel
						  control={
							<Checkbox
							  name={field.key}
							  checked={!!composerData[field.key]} 
							  onChange={handleInputChange}
							  color="primary"
							/>
						  }
						  label={<Typography variant="caption" style={{ fontWeight: "bold" }}>{t(field.labelKey)}</Typography>} 
						/>
					  </Box>
                  );
                }

                // 4. FALL: Dropdowns (Gender, Type)
				if (field.type === "dropdown") {
				  return (
					<Box key={field.key} style={{ marginBottom: "16px" }}>
					  <FormControl variant="outlined" fullWidth size="small">
						<InputLabel id={`${field.key}-label-id`}>{field.label}</InputLabel>
						<Select
						  labelId={`${field.key}-label-id`}
						  id={`${field.key}-select`}
						  name={field.key}
						  // 💡 Validiert nun korrekt gegen opt.key
						  value={
							composerOptions
							  .filter(opt => opt.type === field.optionType)
							  .some(opt => opt.key === composerData[field.key])
							  ? composerData[field.key] 
							  : ""
						  }
						  onChange={handleInputChange}
						  label={<Typography variant="caption" style={{ fontWeight: "bold" }}>{t(field.labelKey)}</Typography>} 
						  MenuProps={{
							style: { zIndex: 3500 },
							disablePortal: true
						  }}
						>
						  <MenuItem value=""><em>{t("common.no_option")}</em></MenuItem>
						  {composerOptions
							.filter(opt => opt.type === field.optionType)
							.map(opt => (
							  // 💡 KORREKTUR: value nutzt jetzt opt.key statt opt.value, damit der Key in die DB wandert
							  <MenuItem key={opt.id} value={opt.key}>
								{getValueByKey(composerOptions, opt.value, userLang, field.optionType)}
							  </MenuItem>
							))}
						</Select>
					  </FormControl>
					</Box>
				  );
				}
                return null;
              })}
            </Box>
          </Paper>


        {/* Aktions-Buttons */}
        <Box style={{ display: "flex", justifyContent: "space-between", gap: "12px", marginTop: "10px" }}>
          <Button variant="outlined" onClick={props.onClose} style={{ borderRadius: "8px", padding: "10px 20px", textTransform: "none" }}>
             {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            disabled={disabled}
            onClick={handleSave}
            style={{ 
              borderRadius: "8px", padding: "10px 20px", textTransform: "none", 
              backgroundColor: disabled ? "#e0e0e0" : "#4f40b3", 
              color: disabled ? "#a6a6a6" : "white" 
            }}
          >
		  {t("common.save")}
          </Button>
        </Box>
		          
        </Box>
      </form>
    </Box>
  );
}

const mapStateToProps = (state) => ({
  userData: state.user.credentials,
});

const mapActionsToProps = {
  editComposer,
  resetData
};

export default connect(mapStateToProps, mapActionsToProps)(MdlComposerEdit);
